import { createSelector, createSlice } from '@reduxjs/toolkit'
import jwtDecode, { JwtPayload } from 'jwt-decode'

import type { RootState } from '../store'
import { getService } from '../../helpers/ReduxHelpers'
import { getAuthCookie } from '../../helpers/CookieHelpers'
import {
  AuthCheckTokenQueryVariables,
  AuthGetMeQueryVariables,
  ForgotPasswordMutationVariables,
  ResetPasswordMutationVariables,
} from '../../graphql/types/api'

import { actionTypes } from './types'
import { ApiResponse } from '../api/types/state'
import { services } from '../../graphql'
//
// Services
//

const loginService = getService<'login', null, { email: string; password: string }>('login', null)
const forgetPasswordService = getService<'forgetPassword', null, ForgotPasswordMutationVariables>(
  'forgetPassword',
  null
)

const resetPasswordService = getService<'resetPassword', null, ResetPasswordMutationVariables>(
  'resetPassword',
  null
)

const getMeService = getService<
  'getMe',
  ApiResponse<typeof services.auth.queries.getMe>['data'],
  AuthGetMeQueryVariables
>('getMe')

const checkTokenService = getService<
  'checkToken',
  ApiResponse<typeof services.auth.queries.checkToken>['data'],
  AuthCheckTokenQueryVariables
>('checkToken')

export type AuthState = {
  impersonate?: boolean
  // services
  login: typeof loginService.values
  forgetPassword: typeof forgetPasswordService.values
  resetPassword: typeof resetPasswordService.values
  getMe: typeof getMeService.values
  checkToken: typeof checkTokenService.values
}

//
// Initial state
//

const initialState: AuthState = {
  impersonate: false,
  // services
  login: loginService.values,
  forgetPassword: forgetPasswordService.values,
  resetPassword: resetPasswordService.values,
  getMe: getMeService.values,
  checkToken: checkTokenService.values,
}

//
// Slice (Actions & Reducers)
//

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuth: () => initialState,
    logout: (_state, _action: actionTypes.logOut) => undefined,
    setToken: (_state, _action: actionTypes.setToken) => undefined,
    setImpersonate: (state, action: actionTypes.setImpersonate) => {
      state.impersonate = action.payload
    },
    // services
    ...loginService.reducers,
    ...forgetPasswordService.reducers,
    ...resetPasswordService.reducers,
    ...getMeService.reducers,
    ...checkTokenService.reducers,
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state[slice.name]
const impersonate = (state: RootState) => root(state).impersonate

const token = () => {
  const authCookie = getAuthCookie()

  return authCookie?.token || null
}

const jwt = createSelector([token], (tokenString): JwtPayload | null => {
  return tokenString ? jwtDecode<JwtPayload>(tokenString) : null
})

const isConnected = () => {
  return !!token()
}

const isExpired = createSelector([jwt], (j): boolean => {
  return !!j && !!j.exp && j.exp * 1000 <= new Date().getTime()
})

const login = (state: RootState) => root(state).login

const forgetPassword = (state: RootState) => root(state).forgetPassword

const resetPassword = (state: RootState) => root(state).resetPassword

const getMe = (state: RootState) => root(state).getMe

const checkToken = (state: RootState) => root(state).checkToken

export const selectors = {
  impersonate,
  token,
  jwt,
  isConnected,
  isExpired,
  login,
  forgetPassword,
  resetPassword,
  getMe,
  checkToken,
}
